import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiKhongHopLeSearchColumns,
  dotThanhToanSearchColumns,
  hopDongSearchColumns,
  khachHangSearchColumns,
  kheUocSearchColumns,
  lenhSanXuatSearchColumns,
  phiNganHangSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  vatTuSearchColumns,
  vuViecSearchColumns
} from '@/constants';
import { CellFieldV2 as CellField, SearchFieldV2 as SearchField } from '@/components/custom/erp';
import { AccountModel, ChiPhiKhongHopLeData, KhachHang, VatTu } from '@/types/schemas';
import type { InputTableCellParams } from '@/components/custom/erp';
import { FormMode } from '@/types/form';

export const getDataTableColumns = (
  onCellValueChange: (rowUuid: string, field: string, newValue: unknown) => void,
  formMode?: FormMode
) => [
  {
    field: 'ma_kh',
    headerName: 'Mã NCC',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<KhachHang>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
        searchColumns={khachHangSearchColumns}
        dialogTitle='Danh mục nhà cung cấp'
        columnDisplay='customer_code'
        value={params.row.ma_kh_data?.customer_code || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_kh_data', row)}
      />
    )
  },
  {
    field: 'ten_kh',
    headerName: 'Tên nhà cung cấp',
    width: 200,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='ten_kh'
        type='text'
        value={params.row.ma_kh_data?.customer_name || ''}
        disabled={formMode === 'view'}
      />
    ),
    nonWritable: true
  },
  {
    field: 'ma_cpnh',
    headerName: 'Mã chi phí',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<Record<string, unknown>>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.PHI_NGAN_HANG}/`}
        searchColumns={phiNganHangSearchColumns}
        columnDisplay='ma_cpnh'
        dialogTitle='Danh mục chi phí'
        value={params.row.ma_cpnh_data?.ma_cpnh || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_cpnh_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_cpnh',
      resultField: 'ma_cpnh_data',
      detailEndpoint: QUERY_KEYS.PHI_NGAN_HANG
    }
  },
  {
    field: 'ten_cpnh',
    headerName: 'Tên chi phí ngân hàng',
    width: 200,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='ten_cpnh'
        type='text'
        value={params.row.ma_cpnh_data?.ten_cpnh || ''}
        disabled={formMode === 'view'}
      />
    ),
    nonWritable: true
  },
  {
    field: 'tien_cp_nt',
    headerName: 'Phí VND',
    width: 150,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='tien_cp_nt'
        type='number'
        value={params.row.tien_cp_nt || 0.0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_cp_nt', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'so_ct0',
    headerName: 'Số hóa đơn',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='so_ct0'
        type='text'
        value={params.row.so_ct0 || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_ct0', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'so_ct2',
    headerName: 'Ký hiệu',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='so_ct2'
        type='text'
        value={params.row.so_ct2 || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_ct2', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'ngay_ct0',
    headerName: 'Ngày hóa đơn',
    width: 150,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='ngay_ct0'
        type='date'
        value={params.row.ngay_ct0 || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ngay_ct0', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'dien_giai',
    headerName: 'Diễn giải',
    width: 200,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='dien_giai'
        type='text'
        value={params.row.dien_giai || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'dien_giai', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'tk_cpnh',
    headerName: 'Tk phí',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<AccountModel>
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay='code'
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_cpnh_data?.code || params.row.ma_cpnh_data?.tk_cpnh_data?.code || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'tk_cpnh_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'tk_cpnh',
      resultField: 'tk_cpnh_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 'tk_du',
    headerName: 'Tk đối ứng',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay='code'
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_du_data?.code || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'tk_du_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'tk_du',
      resultField: 'tk_du_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 'tk_thue',
    headerName: 'Tk thuế',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay='code'
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_thue_data?.code || params.row.ma_cpnh_data?.tk_thue_data?.code || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'tk_thue_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'tk_thue',
      resultField: 'tk_thue_data',
      detailEndpoint: QUERY_KEYS.TAI_KHOAN
    }
  },
  {
    field: 'ma_thue',
    headerName: 'Thuế suất',
    width: 200,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='ma_thue'
        type='text'
        value={params.row.ma_thue_data?.ten_thue}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_thue_data', newValue)}
        disabled={formMode === 'view'}
      />
    ),
    nonWritable: true
  },
  {
    field: 't_thue_nt',
    headerName: 'Thuế VND',
    width: 150,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='t_thue_nt'
        type='number'
        value={params.row.t_thue_nt || 0.0}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 't_thue_nt', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<Record<string, unknown>>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
        searchColumns={boPhanSearchColumns}
        columnDisplay='ma_bp'
        dialogTitle='Danh mục bộ phận'
        value={params.row.ma_bp_data?.ma_bp || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_bp',
      resultField: 'ma_bp_data',
      detailEndpoint: QUERY_KEYS.BO_PHAN
    }
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<Record<string, unknown>>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
        searchColumns={vuViecSearchColumns}
        columnDisplay='ma_vv'
        dialogTitle='Danh mục vụ việc'
        value={params.row.ma_vv_data?.ma_vu_viec || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vv',
      resultField: 'ma_vv_data',
      detailEndpoint: QUERY_KEYS.VU_VIEC
    }
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<Record<string, unknown>>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
        searchColumns={hopDongSearchColumns}
        columnDisplay='ma_hd'
        dialogTitle='Danh mục hợp đồng'
        value={params.row.ma_hd_data?.ma_hd || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_hd_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_hd',
      resultField: 'ma_hd_data',
      detailEndpoint: QUERY_KEYS.HOP_DONG
    }
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<Record<string, unknown>>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
        searchColumns={dotThanhToanSearchColumns}
        columnDisplay='ma_dtt'
        dialogTitle='Danh mục đợt thanh toán'
        value={params.row.ma_dtt_data?.ma_dtt || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_dtt_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_dtt',
      resultField: 'ma_dtt_data',
      detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
    }
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<Record<string, unknown>>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
        searchColumns={kheUocSearchColumns}
        columnDisplay='ma_ku'
        dialogTitle='Danh mục khế ước'
        value={params.row.ma_ku_data?.ma_ku || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_ku_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_ku',
      resultField: 'ma_ku_data',
      detailEndpoint: QUERY_KEYS.KHE_UOC
    }
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<Record<string, unknown>>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.PHI}/`}
        searchColumns={phiSearchColumns}
        columnDisplay='ma_phi'
        dialogTitle='Danh mục phí'
        value={params.row.ma_phi_data?.ma_phi || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_phi',
      resultField: 'ma_phi_data',
      detailEndpoint: QUERY_KEYS.PHI
    }
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<VatTu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
        searchColumns={vatTuSearchColumns}
        columnDisplay='ma_vt'
        dialogTitle='Danh mục sản phẩm'
        value={params.row.ma_sp_data?.ma_vt || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_sp_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_sp',
      resultField: 'ma_sp_data',
      detailEndpoint: QUERY_KEYS.VAT_TU
    }
  },
  {
    field: 'ma_lsx',
    headerName: 'Lệnh sản xuất',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<Record<string, unknown>>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.LENH_SAN_XUAT}/`}
        searchColumns={lenhSanXuatSearchColumns}
        columnDisplay='ma_lsx'
        dialogTitle='Danh mục lệnh sản xuất'
        value={params.row.ma_lsx_data?.ma_lsx || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_lsx_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_lsx',
      resultField: 'ma_lsx_data',
      detailEndpoint: QUERY_KEYS.LENH_SAN_XUAT
    }
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<ChiPhiKhongHopLeData>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.CHI_PHI_KHONG_HOP_LE}/`}
        searchColumns={chiPhiKhongHopLeSearchColumns}
        columnDisplay='ma_cp_khl'
        dialogTitle='Danh mục chi phí không hợp lệ'
        value={params.row.ma_cp0_data?.ma_cpkhl || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: unknown) => onCellValueChange(params.row.uuid, 'ma_cp0_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_cp0',
      resultField: 'ma_cp0_data',
      detailEndpoint: QUERY_KEYS.CHI_PHI_KHONG_HOP_LE
    }
  }
];
